import { useEffect, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { PageHeader } from "../components/Bits";
import { Calendar, X, ChevronLeft, ChevronRight, Images, FolderOpen } from "lucide-react";

function fmtDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

export default function Gallery() {
  const [events, setEvents] = useState([]);
  const [folder, setFolder] = useState(null); // opened folder (event)
  const [light, setLight] = useState(null);    // fullscreen image index

  useEffect(() => { api.get("/events").then((r) => setEvents(r.data)); }, []);

  useEffect(() => {
    if (light === null) return;
    const imgs = folder?.images || [];
    const onKey = (e) => {
      if (e.key === "Escape") setLight(null);
      else if (e.key === "ArrowLeft") setLight((i) => (i - 1 + imgs.length) % imgs.length);
      else if (e.key === "ArrowRight") setLight((i) => (i + 1) % imgs.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [light, folder]);

  const cover = (ev) => ev.thumbnail || ev.images[0];

  return (
    <div data-testid="gallery-page">
      <PageHeader title="Photo Gallery" subtitle="Browse the Association's events. Open a folder to view all photographs of that occasion." />

      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {events.map((ev) => (
            <button key={ev.id} onClick={() => setFolder(ev)} className="card-elite rounded-xl overflow-hidden text-left fade-up flex flex-col" data-testid={`folder-${ev.id}`}>
              <div className="relative aspect-[4/3] overflow-hidden bg-[#0B2E63]/5">
                {cover(ev) ? (
                  <img src={fileUrl(cover(ev))} alt={ev.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#8A6320]"><FolderOpen size={44} /></div>
                )}
                <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-[#071B3D]/90 text-[#D4AF37] text-[11px] font-semibold px-2.5 py-1 rounded-full">
                  <Images size={12} /> {ev.images.length}
                </span>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-[#8A6320]"><Calendar size={13} /> {fmtDate(ev.date)}</div>
                <h3 className="font-display text-xl font-bold text-[#0B2E63] mt-1">{ev.title}</h3>
                {ev.description && <p className="text-sm text-[#5A6270] mt-2 leading-relaxed line-clamp-3">{ev.description}</p>}
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#123B7A]"><FolderOpen size={15} /> Open folder</span>
              </div>
            </button>
          ))}
        </div>
        {events.length === 0 && <p className="text-center text-[#5A6270]">No folders uploaded yet.</p>}
      </section>

      {/* Folder view — grid of all pictures + occasion message */}
      {folder && (
        <div className="fixed inset-0 z-[55] bg-black/70 overflow-y-auto p-4 sm:p-8" onClick={() => setFolder(null)} data-testid="folder-modal">
          <div className="max-w-6xl mx-auto bg-[#FDFBF7] rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-[#071B3D] text-white px-6 py-5 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-[#D4AF37]"><Calendar size={13} /> {fmtDate(folder.date)}</div>
                <h3 className="font-display text-2xl font-bold mt-1">{folder.title}</h3>
                {folder.description && <p className="text-[#cdd6e6] mt-2 font-garamond text-lg max-w-3xl">{folder.description}</p>}
              </div>
              <button onClick={() => setFolder(null)} className="text-white/80 hover:text-white shrink-0" data-testid="folder-close"><X size={26} /></button>
            </div>
            <div className="p-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {folder.images.map((img, i) => (
                <button key={i} onClick={() => setLight(i)} className="group relative aspect-[4/3] overflow-hidden rounded-lg border border-[#E5DECF]" data-testid={`folder-img-${i}`}>
                  <img src={fileUrl(img)} alt={folder.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                </button>
              ))}
              {folder.images.length === 0 && <p className="col-span-full text-center text-[#5A6270] py-8">No pictures in this folder yet.</p>}
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen viewer */}
      {folder && light !== null && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={() => setLight(null)} data-testid="lightbox">
          <button className="absolute top-5 right-5 text-white/80 hover:text-white" onClick={() => setLight(null)}><X size={30} /></button>
          <button className="absolute left-4 sm:left-8 text-white/80 hover:text-white" onClick={(e) => { e.stopPropagation(); setLight((i) => (i - 1 + folder.images.length) % folder.images.length); }}><ChevronLeft size={40} /></button>
          <div className="max-w-4xl w-full text-center" onClick={(e) => e.stopPropagation()}>
            <img src={fileUrl(folder.images[light])} alt={folder.title} className="max-h-[75vh] mx-auto rounded-lg object-contain" />
            <div className="text-[#e6d9bd] mt-4 font-garamond text-lg">{folder.title} — {fmtDate(folder.date)}</div>
            <div className="text-white/50 text-sm">{light + 1} / {folder.images.length}</div>
          </div>
          <button className="absolute right-4 sm:right-8 text-white/80 hover:text-white" onClick={(e) => { e.stopPropagation(); setLight((i) => (i + 1) % folder.images.length); }}><ChevronRight size={40} /></button>
        </div>
      )}
    </div>
  );
}
